import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Download, Loader2, Folder, FolderOpen, ChevronRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, getToken } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { MaskLine, SectionLabel } from "./Reveal";

const CLASSES = ["Class 10", "Class 12"];
const CATEGORIES = ["Question Papers", "Chapter-wise Handouts"];

export default function Handouts() {
  const { user, promptLogin } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [path, setPath] = useState([]); // [] | [cls] | [cls, category]

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/notes");
        if (alive) setNotes(data);
      } catch {
        /* ignore */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const counts = useMemo(() => {
    const map = {};
    for (const c of CLASSES) {
      map[c] = { total: 0 };
      for (const cat of CATEGORIES) map[c][cat] = 0;
    }
    for (const n of notes) {
      if (!map[n.cls]) continue;
      map[n.cls].total += 1;
      if (map[n.cls][n.category] !== undefined) map[n.cls][n.category] += 1;
    }
    return map;
  }, [notes]);

  const filtered = useMemo(() => {
    if (path.length < 2) return [];
    return notes.filter((n) => n.cls === path[0] && n.category === path[1]);
  }, [notes, path]);

  const doDownload = async (note) => {
    setDownloadingId(note.id);
    try {
      const res = await api.get(`/notes/${note.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = note.original_filename || `${note.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) {
      if (e.response?.status === 401) promptLogin(() => doDownload(note));
      else toast.error("Could not download the file. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownload = (note) => {
    if (!getToken() || !user) {
      promptLogin(() => doDownload(note));
      return;
    }
    doDownload(note);
  };

  const renderView = () => {
    if (loading) {
      return (
        <div className="flex items-center gap-3 text-ink/50 py-10" data-testid="notes-loading">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading materials...
        </div>
      );
    }

    // ROOT — class folders
    if (path.length === 0) {
      return (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="folders-root">
          {CLASSES.map((cls, i) => (
            <motion.button
              key={cls}
              data-testid={`folder-${cls.replace(/\s+/g, "-")}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8%" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              whileHover={{ y: -4 }}
              onClick={() => setPath([cls])}
              className="group text-left border border-ink/10 bg-white p-8 hover:border-flame transition-colors duration-300"
            >
              <div className="flex items-start justify-between">
                <span className="grid place-items-center h-14 w-14 rounded-xl bg-ink text-white group-hover:bg-flame transition-colors duration-300">
                  <Folder className="h-7 w-7" />
                </span>
                <ChevronRight className="h-5 w-5 text-ink/30 group-hover:text-flame group-hover:translate-x-1 transition-all" />
              </div>
              <h3 className="mt-6 font-heading font-bold text-2xl text-ink tracking-tight">
                {cls}
              </h3>
              <p className="mt-1 text-sm text-ink/50">
                {counts[cls].total} {counts[cls].total === 1 ? "file" : "files"}
              </p>
            </motion.button>
          ))}
        </div>
      );
    }

    // CLASS — category folders
    if (path.length === 1) {
      const cls = path[0];
      return (
        <div className="grid sm:grid-cols-2 gap-4" data-testid={`folders-${cls.replace(/\s+/g, "-")}`}>
          {CATEGORIES.map((cat, i) => (
            <motion.button
              key={cat}
              data-testid={`subfolder-${cat.replace(/\s+/g, "-").replace(/-wise/, "")}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              whileHover={{ y: -4 }}
              onClick={() => setPath([cls, cat])}
              className="group text-left border border-ink/10 bg-white p-8 hover:border-flame transition-colors duration-300"
            >
              <div className="flex items-start justify-between">
                <span className="grid place-items-center h-14 w-14 rounded-xl bg-flame/15 text-flame group-hover:bg-flame group-hover:text-white transition-colors duration-300">
                  <FolderOpen className="h-7 w-7" />
                </span>
                <ChevronRight className="h-5 w-5 text-ink/30 group-hover:text-flame group-hover:translate-x-1 transition-all" />
              </div>
              <h3 className="mt-6 font-heading font-bold text-xl text-ink tracking-tight">
                {cat}
              </h3>
              <p className="mt-1 text-sm text-ink/50">
                {counts[cls][cat]} {counts[cls][cat] === 1 ? "file" : "files"}
              </p>
            </motion.button>
          ))}
        </div>
      );
    }

    // CATEGORY — file list
    if (filtered.length === 0) {
      return (
        <div
          data-testid="notes-empty"
          className="border border-dashed border-ink/20 rounded-2xl py-16 px-6 text-center"
        >
          <FileText className="h-10 w-10 text-flame mx-auto" />
          <p className="mt-4 font-heading font-bold text-xl text-ink">Nothing here yet</p>
          <p className="mt-2 text-ink/55 text-sm max-w-md mx-auto">
            Files for this folder will appear once uploaded by the academy.
          </p>
        </div>
      );
    }
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((n, i) => (
          <motion.div
            key={n.id}
            data-testid={`note-${n.id}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
            className="group border border-ink/10 bg-white p-6 hover:border-flame transition-colors duration-300 flex flex-col"
          >
            <div className="flex items-center justify-between">
              <FileText className="h-8 w-8 text-flame" />
              <span className="text-xs uppercase tracking-widest text-ink/40">{n.cls}</span>
            </div>
            <h3 className="mt-5 font-heading font-bold text-xl text-ink leading-snug">
              {n.title}
            </h3>
            <div className="mt-1 text-sm text-ink/50">{n.subject}</div>
            <button
              onClick={() => handleDownload(n)}
              disabled={downloadingId === n.id}
              data-testid={`download-note-${n.id}`}
              className="mt-6 inline-flex items-center justify-center gap-2 bg-ink text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-flame transition-colors duration-300 disabled:opacity-60"
            >
              {downloadingId === n.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {downloadingId === n.id ? "Preparing..." : "Download"}
            </button>
          </motion.div>
        ))}
      </div>
    );
  };

  return (
    <section id="handouts" data-testid="handouts-section" className="bg-paper py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
          <div className="lg:col-span-8">
            <SectionLabel>Study Materials</SectionLabel>
            <h2 className="font-heading font-extrabold tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1]">
              <MaskLine>Question papers &amp;</MaskLine>
              <MaskLine delay={0.1}>
                chapter <span className="text-flame">handouts.</span>
              </MaskLine>
            </h2>
          </div>
          <p className="lg:col-span-4 text-ink/60 text-lg">
            Browse folders by class. Log in with your student account to download PDFs.
          </p>
        </div>

        {/* Breadcrumb */}
        {path.length > 0 && (
          <div className="mb-6 flex items-center gap-2 text-sm" data-testid="breadcrumb">
            <button
              onClick={() => setPath(path.slice(0, -1))}
              data-testid="crumb-back"
              className="inline-flex items-center gap-1 text-ink/60 hover:text-flame transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <span className="text-ink/30">/</span>
            <button
              onClick={() => setPath([])}
              className="text-ink/60 hover:text-flame transition-colors"
              data-testid="crumb-root"
            >
              Study Materials
            </button>
            {path.map((p, i) => (
              <span key={i} className="inline-flex items-center gap-2">
                <span className="text-ink/30">/</span>
                <button
                  onClick={() => setPath(path.slice(0, i + 1))}
                  className={`transition-colors ${
                    i === path.length - 1 ? "text-ink font-semibold" : "text-ink/60 hover:text-flame"
                  }`}
                  data-testid={`crumb-${i}`}
                >
                  {p}
                </button>
              </span>
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={path.join("/") || "root"}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {renderView()}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
